import React, { useState } from 'react';
import { Box, Typography, Paper, Collapse, IconButton, Link } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LaunchIcon from '@mui/icons-material/Launch';

const ReferenceMarker = ({ referenceIndex, referenceText }) => {
  const [expanded, setExpanded] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });

  // Close popup when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (expanded && !event.target.closest('[data-reference-popup]')) {
        setExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expanded]);

  if (!referenceText) return null;

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!expanded) {
      // Calculate position when opening
      const rect = e.currentTarget.getBoundingClientRect();
      const popupWidth = 350; // Approximate width
      const popupHeight = 200; // Approximate height
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let left = rect.left;
      let top = rect.bottom + 5;

      // Adjust if popup would go off-screen
      if (left + popupWidth > viewportWidth) {
        left = viewportWidth - popupWidth - 10;
      }

      if (top + popupHeight > viewportHeight) {
        top = rect.top - popupHeight - 5;
      }

      setPopupPosition({ top, left });
    }
    setExpanded(!expanded);
  };

  const isUrl = (text) => {
    const urlPattern = /^https?:\/\/.+/i;
    return urlPattern.test(text.trim());
  };

  const extractUrls = (text) => {
    const urlPattern = /https?:\/\/[^\s,]+/gi;
    return text.match(urlPattern) || [];
  };

  const renderContent = (item) => {
    const cleanItem = item.trim().replace(/^File:\s*/, '');
    
    if (isUrl(cleanItem)) {
      return (
        <Link
          href={cleanItem}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: 'text.secondary',
            textDecoration: 'none',
            fontSize: '0.75rem',
            lineHeight: 1.3,
            wordBreak: 'break-all',
            display: 'flex',
            alignItems: 'left',
            gap: 0.5,
            '&:hover': {
              textDecoration: 'underline',
              color: 'text.primary',
            },
          }}
        >
          {cleanItem}
          <LaunchIcon sx={{ fontSize: '0.75rem', opacity: 0.7 }} />
        </Link>
      );
    }
    
    return (
      <Typography 
        variant="body2" 
        sx={{ 
          color: 'text.secondary', 
          fontSize: '0.75rem', 
          lineHeight: 1.3,
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          maxWidth: '100%',
          whiteSpace: 'normal',
          hyphens: 'auto',
          display: 'block',
        }}
      >
        {cleanItem}
      </Typography>
    );
  };

  return (
    <Box
      component="span"
      sx={{
        display: 'inline',
        position: 'relative',
        verticalAlign: 'baseline',
      }}
    >
      <Box
        component="sup"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleToggle(e);
        }}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '60px',
          height: '18px',
          px: 0.5,
          fontSize: '0.7rem',
          fontWeight: 600,
          color: 'white',
          bgcolor: 'primary.main',
          borderRadius: '3px',
          ml: 0.3,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          lineHeight: 1,
          textAlign: 'center',
          verticalAlign: 'baseline',
          position: 'relative',
          zIndex: 1000,
          whiteSpace: 'nowrap',
          '&:hover': {
            bgcolor: 'primary.dark',
            transform: 'scale(1.05)',
          },
        }}
      >
        References...
      </Box>

      <Collapse in={expanded} timeout={200}>
        <Paper
          data-reference-popup
          elevation={3}
          sx={{
            position: 'fixed',
            top: `${popupPosition.top}px`,
            left: `${popupPosition.left}px`,
            p: 2,
            width: 'min(350px, calc(100vw - 32px))',
            maxWidth: '350px',
            maxHeight: 'min(300px, calc(100vh - 32px))',
            overflowY: 'auto',
            zIndex: 99999,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                fontSize: '0.875rem',
                p: 1,
              }}
            >
              References:
            </Typography>
            <IconButton size="small" onClick={handleToggle} sx={{ p: 0.25 }}>
              <CloseIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Box>
          
          <Box sx={{ px: 1 }}>
            {referenceText.split(';').map((item, index) => {
              const cleanItem = item.trim().replace(/^File:\s*/, '');
              const urls = extractUrls(cleanItem);
              const hasUrls = urls.length > 0;
              
              return (
                <Box key={index} sx={{ mb: 0.75, px: 1 }}>
                  {hasUrls ? (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: 'primary.main',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          mb: 0.5,
                        }}
                      >
                        URLS:
                      </Typography>
                      {urls.map((url, urlIndex) => (
                        <Box key={urlIndex} sx={{ mb: 0.5, textAlign: 'left' }}>
                          <Link
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: 'text.secondary',
                              textDecoration: 'none',
                              fontSize: '0.75rem',
                              lineHeight: 1.3,
                              wordBreak: 'break-all',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              '&:hover': {
                                textDecoration: 'underline',
                                color: 'text.primary',
                              },
                            }}
                          >
                            {url}
                            <LaunchIcon sx={{ fontSize: '0.75rem', opacity: 0.7 }} />
                          </Link>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    renderContent(cleanItem)
                  )}
                </Box>
              );
            })}
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default ReferenceMarker;

